/**
 * The settings panel: palette, theme, density, text size — and the Screener.
 *
 * EVERY CHANGE APPLIES IMMEDIATELY, AND THERE IS NO SAVE BUTTON. The four
 * values go on `<html>` as attributes, so picking Phosphor repaints the list,
 * the reading pane and this panel in the same frame. That is not a flourish: a
 * ground colour cannot be judged from a 44x30 swatch, and a picker that asks you
 * to commit before you can see the result is one nobody can actually choose
 * with. The same mechanism gives the text-size ladder its preview for free —
 * `--fs` is on the root and this panel is sized in `rem`, so choosing XXXL
 * enlarges the panel you chose it in.
 *
 * REAL RADIOS, NOT CLICKABLE DIVS. Four `fieldset`s, one `name` per group, so
 * the arrow keys walk each ladder, the group takes its accessible name from its
 * `legend`, and each option is announced with its position in the group — none
 * of which we write a line of code for. The alternative, divs with
 * `role="radio"`, means hand-rolling roving tabindex once per ladder, and every
 * hand-rolled copy is a separate place for it to be subtly wrong.
 *
 * THE FOUR LADDERS AND THE PAGE SIZE NEVER LEAVE THIS BROWSER. `appearance.ts`
 * owns their storage and it is localStorage only — `colour.md` is explicit that
 * a shared family laptop must not repaint when somebody switches account. This
 * panel is the only thing that writes them, which is why the state below is
 * read once on mount and never re-read.
 *
 * THE ADDRESSES SECTION IS THE OTHER ACCOUNT-SCOPED THING HERE, and it is below
 * the note that says so for that reason. It is in this panel rather than in a
 * panel of its own because of what it replaced: `addresses.masked` and
 * `addresses.alias` were granted to owner and adult and gated nothing at all, so
 * the only way to reach an alias or a disposable address was `admin.server` —
 * tailnet-only, and the key to the whole warden including `master.rotate`. A
 * separate destination for it would need a route, a keybinding and a place in
 * the shell to be pressed from; this is the drawer every reader already knows
 * how to open, and a feature nobody can press is the failure this repository
 * keeps shipping.
 *
 * THE SCREENER SWITCH IS THE EXCEPTION, and it is marked as one on screen. It
 * is the account's — the origin gates the whole Screener Sieve block on
 * `/screener/enabled`, and until this control existed nothing in this client
 * could write that leaf, so a household could triage cards for weeks while
 * every message carried on landing exactly where it always had. It is here
 * rather than in the Screener itself because it is a thing you decide once, and
 * this is where the things you decide once already live.
 */
import { useEffect, useId, useRef, useState } from 'react'
import { palettes, paletteNames, type ThemeMode, type TokenName } from '@hearth/tokens'
import {
  readAppearance,
  save as saveAppearance,
  type Appearance,
  clampPageSize,
  MAX_PAGE_SIZE,
  MIN_PAGE_SIZE,
  type Density,
  type PageSize,
  type Theme,
} from './appearance.ts'
import { api, errorText, type Whoami } from './api.ts'
import { Addresses } from './Addresses.tsx'
import { useWebPush } from './usePush.ts'
import { notReadyRefusal, readScreenerEnabled, writeScreenerEnabled } from './settings.ts'
import './settings.css'

export interface SettingsProps {
  readonly open: boolean
  readonly onClose: () => void
  /**
   * Told when the page size changes, because it is the one setting here that is
   * not about appearance: it changes what the client ASKS THE SERVER FOR, so
   * the shell has to re-run its search rather than re-render what it holds.
   */
  readonly onPageSize?: (size: PageSize) => void
  /**
   * Who the origin says this is, as the shell already read it.
   *
   * THE ADDRESSES SECTION USED TO ASK AGAIN. `app.tsx` fetches `/whoami` once on
   * mount and derives the whole client's `may()` from it; `Addresses` issued its
   * own `GET /whoami` every time this drawer opened — a second round trip for an
   * answer already in memory, and two copies of one fact that can be read at
   * different moments and disagree. A role changed between the two reads gates
   * the palette one way and the addresses panel another.
   *
   * OPTIONAL, because the drawer is rendered in tests without a shell around it
   * and `/whoami` may not have answered yet in the browser. Absent means nothing
   * is known, which gates nothing — the origin refuses what it refuses.
   */
  readonly me?: Whoami | null
}

interface Option<T extends string | number> {
  readonly value: T
  readonly label: string
}

/**
 * `system` is a state, not a value, and it has to be reachable from the UI.
 *
 * `apply()` DELETES `data-theme` for it rather than writing the word, because
 * the dark rules are guarded as `:not([data-theme="light"])` — the absence of
 * the attribute is what hands the decision to the OS. A two-button light/dark
 * toggle can never reach that branch again once somebody has pressed either
 * side of it, which is the whole reason the third option is here.
 */
const THEMES: readonly Option<Theme>[] = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'system', label: 'Match system' },
]

/** Common answers, offered as one click. Any other number is typed. */
const PAGE_SIZE_PRESETS: readonly number[] = [25, 50, 100, 200]

const DENSITIES: readonly Option<Density>[] = [
  { value: 'compact', label: 'Compact' },
  { value: 'cozy', label: 'Cozy' },
  { value: 'comfortable', label: 'Comfortable' },
]

/**
 * Seven steps, 1..7, XS to XXXL.
 *
 * The LABELS are here; the SIZES are not. Each step's multiplier lives once, in
 * the `[data-fs="n"]` blocks the token stylesheet emits, and repeating them here
 * to size the labels would be a second copy that drifts the first time somebody
 * widens the ladder. The preview is the panel itself resizing.
 */
const FONT_SCALES: readonly Option<number>[] = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'].map(
  (label, index) => ({ value: index + 1, label }),
)

export function Settings({ open, onClose, onPageSize, me }: SettingsProps): React.JSX.Element | null {
  const ids = useId()
  const [appearance, setAppearance] = useState(readAppearance)
  const panelRef = useRef<HTMLDivElement>(null)
  const returnTo = useRef<HTMLElement | null>(null)
  const prefersDark = usePrefersDark()

  const change = (next: Appearance): void => {
    setAppearance(next)
    // Applies to the document AND persists, in that order, and the persist half
    // is allowed to fail — see `appearance.ts`.
    saveAppearance(next)
  }

  // ESCAPE IS BOUND ON THE DOCUMENT, not on the panel, because the panel is not
  // guaranteed to hold focus: the reader can click the scrim, and a browser
  // extension can move focus out from under us. Bound only while open, so a
  // closed panel that a parent leaves mounted does not swallow Escape from the
  // message list underneath.
  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, onClose])

  /**
   * Focus goes in on open and comes back out on close.
   *
   * The opener is captured ONLY IF IT IS OUTSIDE THE PANEL, and that condition
   * is load-bearing under StrictMode: effects run, clean up and run again, so
   * the second run sees the panel itself as the active element. Without the
   * containment check the panel would record itself as the thing to return
   * focus to, and closing would strand focus on a node that has just been
   * removed — which sends it to `<body>` and loses the reader's place.
   */
  useEffect(() => {
    if (!open) return
    const panel = panelRef.current
    const opener = document.activeElement
    if (opener instanceof HTMLElement && !panel?.contains(opener)) returnTo.current = opener
    panel?.focus()
    return () => returnTo.current?.focus()
  }, [open])

  if (!open) return null

  const mode: ThemeMode = appearance.theme === 'system' ? (prefersDark ? 'dark' : 'light') : appearance.theme

  return (
    <div
      className="settings-scrim"
      // ONLY A CLICK WHOSE TARGET IS THE SCRIM ITSELF. Every click inside the
      // panel bubbles through here, so without the test, picking a palette
      // would close the panel you are picking it in.
      //
      // A drag that starts inside the panel and is released over the scrim is
      // still a close, because the click event fires at the common ancestor of
      // the two — the usual mousedown-target guard is not here. It costs
      // nothing in this panel: every choice was applied and persisted the
      // moment it was made, so closing discards precisely nothing.
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        className="settings-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${ids}-title`}
        tabIndex={-1}
        ref={panelRef}
        onKeyDown={(event) => trapTab(event, panelRef.current)}
      >
        <header className="settings-head">
          {/* NOT "Appearance" ANY MORE. The Screener switch below is not about
              how anything looks, and a heading that says it is would be the
              reason somebody never finds it. */}
          <h2 className="settings-title" id={`${ids}-title`}>
            Settings
          </h2>
          <button type="button" className="settings-close" aria-label="Close settings" onClick={onClose}>
            &#215;
          </button>
        </header>

        <div className="settings-body">
          <fieldset className="settings-group">
            <legend className="settings-legend">Palette</legend>
            <div className="palette-list">
              {paletteNames.map((name) => {
                const palette = palettes[name]
                if (!palette) return null
                return (
                  <label className="palette-option" key={name} data-checked={name === appearance.palette}>
                    <input
                      type="radio"
                      name="hearth-palette"
                      value={name}
                      checked={name === appearance.palette}
                      onChange={() => change({ ...appearance, palette: name })}
                      // The accessible NAME is the palette's name and the
                      // tagline is its DESCRIPTION. Left to the wrapping label,
                      // the two would fuse into a single name of up to 54
                      // characters ("Phosphor terminal-adjacent, with a
                      // printout light mode"), read out in full five times over
                      // as somebody arrows down the list.
                      aria-labelledby={`${ids}-palette-${name}`}
                      aria-describedby={`${ids}-tagline-${name}`}
                    />
                    <Swatch name={name} tokens={palette[mode]} />
                    <span className="palette-text">
                      <span className="palette-name" id={`${ids}-palette-${name}`}>
                        {name}
                      </span>
                      <span className="palette-tagline" id={`${ids}-tagline-${name}`}>
                        {description(name, palette.tagline)}
                      </span>
                    </span>
                  </label>
                )
              })}
            </div>
          </fieldset>

          <Ladder
            legend="Theme"
            name="hearth-theme"
            value={appearance.theme}
            options={THEMES}
            onPick={(theme) => change({ ...appearance, theme })}
          />

          <Ladder
            legend="Density"
            name="hearth-density"
            value={appearance.density}
            options={DENSITIES}
            onPick={(density) => change({ ...appearance, density })}
          />

          <Ladder
            legend="Text size"
            name="hearth-font-scale"
            value={appearance.fontScale}
            options={FONT_SCALES}
            onPick={(fontScale) => change({ ...appearance, fontScale })}
          />

          <PageSizeField
            value={appearance.pageSize}
            onPick={(pageSize) => {
              change({ ...appearance, pageSize })
              onPageSize?.(pageSize)
            }}
          />

          {/* SAYS WHICH SETTINGS IT IS TALKING ABOUT, now that the panel holds
              one that is not. "These" read as "everything in this panel" while
              everything in this panel was device-scoped; below the Screener
              switch it would be a plain untruth. */}
          <p className="settings-note">
            Everything above is remembered for this browser, not for your account — switching account does not repaint a
            shared machine.
          </p>

          <Addresses me={me ?? null} />
          <ScreenerSwitch />
          <NotificationSwitch />
        </div>
      </div>
    </div>
  )
}

/**
 * How many messages a page holds.
 *
 * PRESETS PLUS A FREE NUMBER, because the right value depends on the screen and
 * the archive rather than on anything this code knows — and because a fixed
 * ladder is what turned this setting into an argument in the first place.
 *
 * COMMITTED ON BLUR AND ON ENTER, NOT ON EVERY KEYSTROKE. Typing "250" passes
 * through "2" and "25", and applying each of those re-runs the search — three
 * round trips and a list that flickers between two rows and twenty-five. The
 * clamp lands at the same moment, so a typed "5" becomes 10 when the reader
 * leaves the field rather than fighting them mid-word.
 */
function PageSizeField({
  value,
  onPick,
}: {
  value: number
  onPick: (size: number) => void
}): React.JSX.Element {
  const [typed, setTyped] = useState(String(value))
  const fieldId = useId()

  // Follow the value when a preset changes it, so the box never disagrees with
  // the buttons beside it.
  useEffect(() => setTyped(String(value)), [value])

  const commit = (): void => {
    const parsed = Number.parseInt(typed, 10)
    if (!Number.isFinite(parsed)) {
      setTyped(String(value))
      return
    }
    const next = clampPageSize(parsed)
    setTyped(String(next))
    if (next !== value) onPick(next)
  }

  return (
    <fieldset className="settings-group">
      <legend>Messages per page</legend>
      <div className="settings-row">
        {PAGE_SIZE_PRESETS.map((preset) => (
          <button
            key={preset}
            type="button"
            className="page-preset"
            aria-pressed={value === preset}
            onClick={() => onPick(preset)}
          >
            {preset}
          </button>
        ))}
        <label className="page-custom" htmlFor={fieldId}>
          <span>or</span>
          <input
            id={fieldId}
            type="number"
            inputMode="numeric"
            min={MIN_PAGE_SIZE}
            max={MAX_PAGE_SIZE}
            value={typed}
            aria-label="Messages per page"
            onChange={(event) => setTyped(event.target.value)}
            onBlur={commit}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                commit()
              }
            }}
          />
        </label>
      </div>
      <p className="settings-note">
        The list loads this many and stops; there is a “Load more” at the bottom for the next {value.toLocaleString()}.
        Between {MIN_PAGE_SIZE} and {MAX_PAGE_SIZE.toLocaleString()} — the server sends at most 200 per request, so a
        larger page is simply several requests.
      </p>
    </fieldset>
  )
}

/**
 * What this client knows about the Screener, which is only ever what the origin
 * just told it.
 *
 * `ready` IS NOT DERIVED HERE AND MUST NOT BE. It is `screenerReady()` — "has
 * the seed found anybody at all" — read straight off `GET /screener`, which is
 * the same answer `PUT /settings` gates the write on. A client that worked
 * readiness out for itself (from the size of the card stack, say) would be a
 * second predicate about the one thing this feature cannot afford two of: the
 * two would disagree on exactly the day-one account where the disagreement
 * empties four Inboxes.
 */
type ScreenerGate =
  | { readonly status: 'loading' }
  | { readonly status: 'unreachable'; readonly why: string }
  | { readonly status: 'known'; readonly enabled: boolean; readonly ready: boolean }

/**
 * The Screener's master switch.
 *
 * WHAT IT WRITES IS THE WHOLE FEATURE. `/screener/enabled` is what the origin
 * compiles the Sieve block on; with it unwritten the cards are a questionnaire
 * whose answers file nothing, and the surface looks like it works.
 *
 * IT IS TWO REQUESTS ON OPEN AND NOT A CACHE, for the reason `settings.ts`
 * gives about the document generally, plus one of its own: `ready` flips on its
 * own when the first sync finishes, so a panel that remembered "not ready" from
 * an hour ago would keep refusing after the reason had gone.
 */
/**
 * Notifications for a tab that is not open.
 *
 * THIS IS THE ONLY PLACE `Notification.requestPermission()` CAN BE CALLED FROM,
 * and that is not a preference — it is the whole feature. Every browser refuses
 * that call outside a user gesture, and on iOS there is no site-settings screen
 * that can grant the permission instead: a button somebody presses is the only
 * route there is. Web Push shipped complete — a service worker, VAPID, a
 * subscription store, an account-scoped fan-out — behind no button at all, so on
 * an iPhone it could never be turned on, and everywhere else it needed the
 * reader to find the browser's own permission UI and then reload.
 *
 * IT REPORTS WHAT IT CANNOT DO RATHER THAN HIDING. `unsupported` on iOS means
 * "not until this is on the Home Screen" and says so; `unconfigured` means the
 * server has no VAPID key, which is an operator's job and not the reader's;
 * `blocked` means the browser is refusing and only its own settings can undo
 * that. A switch that is simply absent teaches somebody the feature does not
 * exist.
 */
function NotificationSwitch(): React.JSX.Element {
  const { status, enable, disable } = useWebPush()
  const [busy, setBusy] = useState(false)
  const noteId = useId()

  const on = status.state === 'on'
  // ONLY THESE TWO CAN BE ACTED ON HERE. The rest are conditions somebody else
  // has to change — the browser, or whoever runs the server — and offering a
  // control for them would be a button that does nothing.
  const actionable = status.state === 'off' || status.state === 'on' || status.state === 'failed'

  return (
    <section className="settings-group">
      <h3 className="settings-legend">Notifications</h3>
      <label className="settings-switch">
        <input
          type="checkbox"
          checked={on}
          disabled={busy || !actionable}
          aria-describedby={noteId}
          onChange={() => {
            setBusy(true)
            void (on ? disable() : enable()).finally(() => setBusy(false))
          }}
        />
        <span>Tell me about new mail when Hearth is closed</span>
      </label>
      <p className="settings-note" id={noteId}>
        {status.detail ??
          (on
            ? 'This device will be told when mail arrives. It carries no subject and no sender — only that there is something new.'
            : 'Off. Hearth tells you about new mail only while it is open in a tab.')}
      </p>
    </section>
  )
}

function ScreenerSwitch(): React.JSX.Element {
  const [gate, setGate] = useState<ScreenerGate>({ status: 'loading' })
  const [busy, setBusy] = useState(false)
  /** What the last write actually achieved. More specific than the note it hides. */
  const [outcome, setOutcome] = useState<string | null>(null)
  const noteId = useId()

  // The panel returns null while closed, so this component mounts on open and
  // unmounts on close: the effect IS the on-open read. `live` is not about
  // React warning — it is so a panel closed while the request is in flight does
  // not come back next time showing an answer to a question asked an hour ago.
  useEffect(() => {
    let live = true
    // ONE CARD, because the only field wanted is `ready`; the stack itself is
    // the Screener's own surface and asking for fifty of it here would be fifty
    // rows fetched to look at a boolean.
    Promise.all([api.screener(1), readScreenerEnabled()])
      .then(([page, enabled]) => {
        if (live) setGate({ status: 'known', enabled, ready: page.ready })
      })
      .catch((error: unknown) => {
        if (live) setGate({ status: 'unreachable', why: errorText(error, 'the server did not answer') })
      })
    return () => {
      live = false
    }
  }, [])

  const set = async (next: boolean): Promise<void> => {
    setBusy(true)
    setOutcome(null)
    try {
      await writeScreenerEnabled(next)
    } catch (error: unknown) {
      const refusal = notReadyRefusal(error)
      // THE ORIGIN HAS JUST SAID IT IS NOT READY, so the control stops offering
      // to switch it on — the local `ready` came from a request that may be
      // minutes old, and the refusal is the newer fact about the same thing.
      if (refusal) setGate((current) => (current.status === 'known' ? { ...current, ready: false } : current))
      setOutcome(refusal ?? `That could not be saved: ${errorText(error, 'the server refused it')}.`)
      setBusy(false)
      return
    }
    setGate((current) => (current.status === 'known' ? { ...current, enabled: next } : current))
    setOutcome(await installFilters(next))
    setBusy(false)
  }

  const enabled = gate.status === 'known' && gate.enabled
  // THE GATE ONLY EVER BLOCKS THE "ON" DIRECTION. Off is reachable from every
  // state there is, including the one where the sender table has gone empty
  // under a switch somebody already turned on — a control that can put a
  // household into "every message is being filed" and not take them back out is
  // worse than no control.
  const locked = busy || gate.status !== 'known' || (!gate.ready && !gate.enabled)

  return (
    <fieldset className="settings-group">
      <legend className="settings-legend">Screener</legend>
      {/* No `data-checked` on this one, unlike the ladders above: a checkbox
          has one member, so the box IS the state and a tinted row beside it
          would be a second thing to keep in agreement with it. */}
      <label className="settings-switch">
        <input
          type="checkbox"
          checked={enabled}
          disabled={locked}
          // The note carries the reason the box is disabled, and a disabled
          // checkbox with no reason attached is the thing people file bugs
          // about. `aria-describedby` is what says it to a screen reader.
          aria-describedby={noteId}
          onChange={(event) => void set(event.target.checked)}
        />
        <span>Screen mail from senders I have not decided about</span>
      </label>
      <p className="settings-note" id={noteId}>
        {outcome ?? gateNote(gate)}
      </p>
    </fieldset>
  )
}

/**
 * What the switch says about itself in each state.
 *
 * THE NOT-READY SENTENCE NAMES THE CONSEQUENCE, not the mechanism. "The sender
 * table is empty" is true and tells a family member nothing; what they need to
 * know is that the Inbox they read on their phone would go empty, and that the
 * wait is finite and needs nothing from them.
 */
function gateNote(gate: ScreenerGate): string {
  if (gate.status === 'loading') return 'Asking the server whether the Screener is ready…'
  if (gate.status === 'unreachable') return `The Screener could not be reached: ${gate.why}.`
  if (!gate.ready) {
    // ON-WITHOUT-A-LIST IS NOT A CATASTROPHE, and this must not say it is. The
    // origin's third lock omits the Screener block from the script entirely
    // while nobody is known, so the honest description of that state is that
    // nothing is happening yet — not that everything is being filed.
    return gate.enabled
      ? 'On, but Hearth has nobody on its known-senders list at the moment, so nothing is being screened until it has. Switching it off stays available.'
      : 'Not yet. Hearth works out who you already know from the mail you have sent, and has not found anybody yet — with that list empty every message would count as a stranger’s, and the Inbox would empty into Hearth/Screener here and in the Mail app on your phone. The list fills itself as the first sync runs; there is nothing to do but wait.'
  }
  return gate.enabled
    ? 'On. A sender you have never decided about waits in Hearth/Screener; everyone you have decided about goes to the Inbox as before.'
    : 'Off. Everything arrives in your Inbox, and the Screener is only a list of new senders you can sort. Turning it on starts filing them.'
}

/**
 * Get the switch into the script that actually runs.
 *
 * THE SETTING ON ITS OWN FILES NOTHING. `PUT /rules` is the only thing in this
 * system that writes a Sieve script — the settings route stores the leaf and
 * never touches ManageSieve — so the block appears, or disappears, when the
 * rule set is next saved and at no other moment. A switch that wrote the leaf
 * and stopped would be a second inert control in the same feature, which is the
 * defect this whole change exists to fix. Re-saving the rule set unchanged is
 * what recompiles the script around the new answer.
 *
 * IT WILL NOT REPLACE A SCRIPT HEARTH DID NOT WRITE. `PUT /rules` overwrites
 * the active script outright; drift means somebody has edited it by hand, or it
 * was never ours. Overwriting that from a toggle in a settings panel is a
 * decision that belongs on the Filters screen, where the difference is shown.
 *
 * IT NEVER THROWS. The setting is stored by the time this runs, and a mail
 * server that would not take the script does not undo somebody's answer — what
 * they get is a sentence saying which half happened.
 */
async function installFilters(enabled: boolean): Promise<string> {
  const stored = enabled ? 'Saved' : 'Saved, and the Screener is off'
  try {
    const { ruleSet, drift } = await api.rules()
    // `unknown` FIRST, AND EXPLICITLY. `GET /rules` now answers 200 with
    // `{state: 'unknown'}` when it cannot reach ManageSieve — before that it
    // threw, and this function's honest catch below said so. Left to the
    // `!== 'clean'` catch-all, an unreachable mail server produces "the filter
    // script on the server is not the one Hearth wrote", which is a diagnosis
    // nobody made, followed by advice ("open Filters and save there") that will
    // itself 502.
    //
    // `Rules.tsx` got this rung when the wire changed and this consumer did not,
    // which is the same two-predicates-about-one-thing the rung exists to stop:
    // one screen learned about the new state and its sibling kept a catch-all
    // that silently absorbed it into the wrong sentence.
    if (drift.state === 'unknown') {
      return `${stored}, but Hearth could not reach the mail server, so it does not know whether the filter script there matches these rules and did not touch it. Nothing was installed; try again when the server is back.`
    }
    if (drift.state !== 'clean') {
      return `${stored}, but the filter script on the server is not the one Hearth wrote, so it was left alone. Open Filters and save there to install it; until then mail is filed the way it was.`
    }
    await api.saveRules(ruleSet)
    return enabled
      ? 'On, and installed. Mail from a sender you have not decided about now waits in Hearth/Screener instead of the Inbox.'
      : 'Off, and installed. Everything arrives in the Inbox again.'
  } catch (error: unknown) {
    return `${stored}, but the filter script could not be installed: ${errorText(error, 'the mail server refused it')}. Until it is, mail is filed the way it was.`
  }
}

/** One `fieldset`, one radio `name`, one row of choices. */
function Ladder<T extends string | number>({
  legend,
  name,
  value,
  options,
  onPick,
}: {
  legend: string
  name: string
  value: T
  options: readonly Option<T>[]
  onPick: (value: T) => void
}): React.JSX.Element {
  return (
    <fieldset className="settings-group">
      <legend className="settings-legend">{legend}</legend>
      <div className="choice-row">
        {options.map((option) => (
          <label className="choice" key={option.value} data-checked={option.value === value}>
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={option.value === value}
              onChange={() => onPick(option.value)}
            />
            <span>{option.label}</span>
          </label>
        ))}
      </div>
    </fieldset>
  )
}

/**
 * A palette in miniature, built from that palette's own tokens.
 *
 * Inline styles rather than classes, and this is the one place in the client
 * where that is right: the whole point is to show FIVE palettes at once, and
 * only one of them is mounted on the document at a time. A class reading
 * `var(--surface)` would give five identical swatches in the current palette.
 *
 * `aria-hidden`, because it says nothing the name and tagline beside it do not,
 * and a screen reader announcing three coloured bars per option is noise.
 */
function Swatch({ name, tokens }: { name: string; tokens: Readonly<Record<TokenName, string>> }): React.JSX.Element {
  return (
    <span
      className="swatch"
      data-swatch={name}
      aria-hidden="true"
      style={{ background: tokens['--surface'], borderColor: tokens['--line-strong'] }}
    >
      <span className="swatch-bar" style={{ background: tokens['--accent'], width: '55%' }} />
      <span className="swatch-bar" style={{ background: tokens['--text'], width: '85%' }} />
      <span className="swatch-bar" style={{ background: tokens['--text-muted'], width: '68%' }} />
    </span>
  )
}

const NAME_SEPARATOR = ' — '

/**
 * The tagline with the name it repeats taken off the front.
 *
 * Every tagline in `palettes.json` is written "Ledger — warm paper, one rust
 * accent": the palette's own name, an em dash, then the description. Printed
 * verbatim beside the name it gives "Ledger / Ledger — warm paper…". The prefix
 * comes off when it is there, and a tagline that is not written that way is used
 * whole rather than being cut at a guess.
 */
function description(name: string, tagline: string): string {
  const prefix = `${name}${NAME_SEPARATOR}`
  return tagline.startsWith(prefix) ? tagline.slice(prefix.length) : tagline
}

/**
 * Which half of each palette the swatches show.
 *
 * Only the swatches need this. Everything else is decided by CSS, which knows
 * `prefers-color-scheme` natively; a swatch is drawing a palette that is NOT
 * mounted on the document, so nothing can resolve it for us.
 *
 * Guarded because jsdom 27.4.0 has no `window.matchMedia` at all — measured,
 * not assumed — and a panel that throws in the one environment its tests run in
 * is a panel with no tests. The fallback is light, which is what the swatch
 * would have shown for a reader whose OS never asked for dark.
 */
function usePrefersDark(): boolean {
  const [dark, setDark] = useState(() => darkQuery()?.matches ?? false)

  useEffect(() => {
    const query = darkQuery()
    if (!query) return
    const onChange = (event: MediaQueryListEvent): void => setDark(event.matches)
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }, [])

  return dark
}

function darkQuery(): MediaQueryList | null {
  return typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-color-scheme: dark)') : null
}

/**
 * Keep Tab inside the panel.
 *
 * `aria-modal="true"` tells a screen reader the rest of the page is inert, and
 * the scrim says the same to the mouse. Without this the keyboard disagrees with
 * both: Tab off the last control lands in the search box behind the scrim, where
 * the reader can type into a field they cannot see.
 *
 * A RADIO GROUP IS ONE TAB STOP, and that is why the list below is filtered
 * rather than taken straight from `querySelectorAll`. The last element in the
 * panel is the XXXL radio, which is not tabbable while another step is chosen —
 * so a trap that compared against it would never fire, and would leak on every
 * Tab. Every group here always has exactly one checked member, so "the checked
 * one" is the complete rule; the unchecked-group case cannot arise.
 */
const FOCUSABLE = 'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])'

function trapTab(event: React.KeyboardEvent<HTMLElement>, panel: HTMLElement | null): void {
  if (event.key !== 'Tab' || !panel) return

  const stops = [...panel.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((element) => {
    if (element.hasAttribute('disabled')) return false
    if (element instanceof HTMLInputElement && element.type === 'radio') return element.checked
    return true
  })

  const first = stops[0]
  const last = stops[stops.length - 1]
  if (!first || !last) return

  const active = document.activeElement
  // The panel container itself is a stop (`tabIndex={-1}`, focused on open), and
  // shift-Tab from it goes backwards out of the panel unless it is caught here.
  if (event.shiftKey && (active === first || active === panel)) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && active === last) {
    event.preventDefault()
    first.focus()
  }
}
